import { Heart, Video, Award, Users, Play, Trash2, Flame, Droplets } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useToggleFavorite } from "@/hooks/useFavorites";
import { useProductEditor } from "@/hooks/useProductEditor";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useState } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { optimizeImageUrl, imageSizes } from "@/lib/imageUtils";
import { requireUser } from "@/lib/requireUser";

interface CourseCardProps {
  id: string;
  slug?: string;
  title: string;
  subtitle: string;
  image: string;
  price: number;
  msrp: number;
  price_duration?: string;
  rating: number;
  review_count: number;
  categories: string[];
  instructor_info?: {
    name: string;
    title?: string;
    avatar_url?: string;
  };
  profile_image?: string;
  total_video_hours?: number;
  certificate_included?: boolean;
  student_count?: number;
  preview_lesson_url?: string;
  skill_level?: string;
  is_published?: boolean;
  isAdmin?: boolean;
  is_hot?: boolean;
  isFromDatabase?: boolean;
  sort_order?: number;
  isFavorited?: boolean; // Passed from parent, not fetched internally
}

export const CourseCard = ({
  id,
  slug,
  title,
  subtitle,
  image,
  price,
  msrp,
  price_duration = 'one-time',
  rating,
  review_count,
  categories,
  instructor_info,
  profile_image,
  total_video_hours,
  certificate_included,
  student_count = 0,
  preview_lesson_url,
  skill_level,
  is_published = true,
  isFromDatabase = false,
  isAdmin = false,
  is_hot = false,
  sort_order = 999,
  isFavorited = false, // Use prop instead of hook
}: CourseCardProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toggleFavorite = useToggleFavorite(slug || id);
  const { updateProduct, deleteProduct } = useProductEditor();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { isEditMode } = useEditMode();

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    requireUser({
      user,
      navigate,
      currentPath: window.location.pathname + window.location.search,
      action: () => toggleFavorite.mutate(),
      preferSignup: true,
    });
  };

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (preview_lesson_url) {
      window.open(preview_lesson_url, '_blank');
    }
  };

  const formatStudentCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const discountPercent = msrp > 0 && price < msrp 
    ? Math.round(((msrp - price) / msrp) * 100) 
    : 0;

  const handleUpdateField = (field: string, value: any) => {
    if (!isFromDatabase) {
      console.warn('Cannot update mock product');
      return;
    }
    updateProduct({ productId: slug || id, field, value });
  };

  const handleTogglePublish = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleUpdateField('is_published', !is_published);
  };

  const handleToggleHot = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleUpdateField('is_hot', !is_hot);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isFromDatabase) {
      console.warn('Cannot delete mock product');
      return;
    }
    deleteProduct(slug || id);
    setShowDeleteDialog(false);
  };

  const handleSortOrderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 999 : parseInt(e.target.value);
    if (!isNaN(value) && isFromDatabase) {
      handleUpdateField('sort_order', value);
    }
  };

  return (
    <Link to={`/product/${slug || id}`} className="h-full block group">
      <div className="bg-card rounded-xl overflow-hidden border transition-all hover:shadow-xl hover:border-primary/30 hover:scale-[1.02] h-full flex flex-col animate-fade-in">
        {/* Course Image with Instructor Avatar */}
        <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-muted to-muted/50">
          <img
            src={optimizeImageUrl(image, imageSizes.card) || '/placeholder.svg'}
            alt={title}
            width={400}
            height={300}
            loading="lazy"
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
          
          {/* Gradient Overlay on Hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          
          {/* Instructor Avatar - Overlayed at bottom left */}
          {(profile_image || instructor_info?.avatar_url) && (
            <div className="absolute -bottom-4 left-4 w-16 h-16 rounded-full border-4 border-card overflow-hidden ring-2 ring-primary/20 shadow-xl z-10">
              <img
                src={optimizeImageUrl(profile_image || instructor_info?.avatar_url, { width: 64, height: 64 }) || '/placeholder.svg'}
                alt={instructor_info?.name || title}
                width={64}
                height={64}
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Sort Order Input - Compact Circle */}
          {isAdmin && isEditMode && (
            <div className="absolute top-2 left-2 z-20 p-2" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
              <input 
                type="number" 
                value={sort_order === 999 ? '' : sort_order}
                placeholder="#"
                onChange={handleSortOrderChange}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                className="w-12 h-12 text-sm font-medium text-center bg-background/95 backdrop-blur-sm border-2 border-border focus:outline-none focus:ring-2 focus:ring-primary rounded-full shadow-md cursor-text"
                min="0"
                title="Sort Order (lower = higher priority)"
              />
            </div>
          )}

          {/* Admin Controls */}
          {isAdmin && isEditMode && (
            <div className="absolute top-2 right-2 flex flex-col md:flex-row gap-1 md:gap-2 z-20">
              {/* Published/Draft Badge */}
              <Badge 
                variant={is_published ? "default" : "secondary"}
                className={`cursor-pointer text-xs font-medium ${
                  is_published 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-amber-500 hover:bg-amber-600 text-white'
                }`}
                onClick={handleTogglePublish}
              >
                {is_published ? 'P' : 'U'}
              </Badge>

              {/* Hot Badge */}
              <Badge 
                variant={is_hot ? "default" : "outline"}
                className={`cursor-pointer text-xs font-medium px-2 py-0.5 ${
                  is_hot 
                    ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                    : 'bg-white hover:bg-orange-50'
                }`}
                onClick={handleToggleHot}
              >
                {is_hot ? <Flame className="h-3 w-3 md:h-4 md:w-4" /> : <Droplets className="h-3 w-3 md:h-4 md:w-4" />}
              </Badge>

              {/* Delete Button */}
              <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-6 px-2"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Course?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this course and all associated data including:
                      <ul className="list-disc list-inside mt-2">
                        <li>Course details</li>
                        <li>All reviews</li>
                        <li>Q&A items</li>
                      </ul>
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {/* Favorite Button - always show */}
          <button
            onClick={handleFavoriteClick}
            className="absolute top-3 right-3 p-2 bg-background/90 backdrop-blur-sm rounded-full hover:bg-background transition-colors z-10 group"
          >
            <Heart className={`h-4 w-4 transition-colors ${isFavorited ? 'fill-destructive text-destructive' : 'text-muted-foreground group-hover:text-red-500'}`} />
          </button>
        </div>

        {/* Card Content */}
        <div className="flex-1 flex flex-col p-4 pt-6">
          {/* Course Badge & Bestseller */}
          <div className="flex items-center gap-2 mb-2.5 flex-wrap">
            <Badge className="bg-course-badge text-white font-semibold text-xs">COURSE</Badge>
            {student_count > 5000 && (
              <Badge className="bg-amber-500 text-white font-semibold text-xs">BESTSELLER</Badge>
            )}
          </div>
          
          {/* Title */}
          <h3 className="font-bold text-base mb-2 line-clamp-2 group-hover:text-primary transition-colors leading-snug">
            {title}
          </h3>

          {/* Instructor */}
          {instructor_info?.name && (
            <p className="text-xs text-muted-foreground mb-3">
              by <span className="font-semibold text-foreground">{instructor_info.name}</span>
              {instructor_info.title && <span className="text-xs"> • {instructor_info.title}</span>}
            </p>
          )}

          {/* Enhanced Stats Row */}
          <div className="flex items-center gap-3 text-xs mb-3 flex-wrap">
            {/* Rating - More Prominent */}
            {rating > 0 && (
              <div className="flex items-center gap-1 bg-amber-50 dark:bg-amber-950/20 px-2 py-1 rounded-md">
                <span className="text-amber-500 text-sm">★</span>
                <span className="font-bold text-foreground">{rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({review_count.toLocaleString()})</span>
              </div>
            )}

            {/* Student Count */}
            {student_count > 0 && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                <span className="font-medium">{formatStudentCount(student_count)}</span>
              </div>
            )}

            {/* Video Hours */}
            {total_video_hours && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Video className="h-3.5 w-3.5" />
                <span className="font-medium">{total_video_hours}hrs</span>
              </div>
            )}

            {/* Certificate */}
            {certificate_included && (
              <div className="flex items-center gap-1 text-course-accent font-medium">
                <Award className="h-3.5 w-3.5" />
                <span>Certificate</span>
              </div>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Price and CTA */}
          <div className="flex items-center justify-between pt-3 border-t mt-auto">
            <div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-xl font-extrabold text-foreground">${price}</span>
                {discountPercent > 0 && (
                  <span className="text-sm text-muted-foreground line-through">
                    ${msrp}
                  </span>
                )}
              </div>
              {discountPercent > 0 && (
                <span className="text-xs text-course-accent font-bold bg-course-accent/10 px-1.5 py-0.5 rounded">
                  {discountPercent}% OFF
                </span>
              )}
            </div>
            <Button size="sm" className="font-bold shadow-md hover:shadow-xl hover:scale-105 transition-all text-xs">
              Enroll Now
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};
